import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ATELIER, FONTS, ICON, MODES } from '../tokens';
import { useWorkspace } from '../WorkspaceContext';

/**
 * IntentChip - quiet, editorial routing label.
 *
 * Render:
 *     routing → image  ⌄
 *
 * • Lowercase monospace label, no pill background by default
 * • The mode word is violet; the rest is muted
 * • Hover reveals a faint dotted underline + chevron
 * • Locked state: a small lock glyph replaces the chevron
 * • A short violet "swap" pulse plays under the mode word when auto-detect
 *   flips to a new modality (so the user feels the routing happen)
 */
export function IntentChip() {
  const { intent, intentLocked, overrideIntent, draftPrompt } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const ref = useRef(null);
  const prevModeRef = useRef(intent.mode);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pulse on auto-detected mode change (only while typing)
  useEffect(() => {
    if (prevModeRef.current !== intent.mode && draftPrompt.trim() && !intentLocked) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 700);
      prevModeRef.current = intent.mode;
      return () => clearTimeout(t);
    }
    prevModeRef.current = intent.mode;
  }, [intent.mode, draftPrompt, intentLocked]);

  // Outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const m = MODES.find(x => x.id === intent.mode) || MODES[0];
  const [hover, setHover] = useState(false);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={intentLocked
          ? `Routing locked to ${m.label}`
          : `Auto-routed to ${m.label} (${intent.source})`}
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 5,
          height: 30,
          padding: isMobile ? '0 4px' : '0 8px',
          background: hover ? ATELIER.bgHover : 'transparent',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontFamily: FONTS.mono,
          fontSize: 11.5,
          fontWeight: 500,
          letterSpacing: '0.02em',
          color: ATELIER.inkSoft,
          transition: 'background 0.15s',
        }}
      >
        {!isMobile && (
          <>
            <span style={{ opacity: 0.78 }}>routing</span>
            <span style={{ opacity: 0.5, padding: '0 1px' }}>→</span>
          </>
        )}
        <span
          style={{
            position: 'relative',
            color: ATELIER.accent,
            fontWeight: 600,
            letterSpacing: '0.01em',
          }}
        >
          {m.id}
          {/* Swap pulse - a violet underline that sweeps left-to-right */}
          {pulse && (
            <motion.span
              aria-hidden
              initial={{ scaleX: 0, opacity: 1 }}
              animate={{ scaleX: 1, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'absolute',
                left: 0, right: 0, bottom: -2,
                height: 1.5,
                background: ATELIER.accent,
                transformOrigin: 'left center',
                borderRadius: 1,
              }}
            />
          )}
          {/* Static dotted underline (always visible to signal interactivity) */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0, right: 0, bottom: -2,
              height: 1,
              borderBottom: `1px dotted ${ATELIER.accent}`,
              opacity: hover ? 0.9 : 0.45,
              transition: 'opacity 0.15s',
            }}
          />
        </span>
        {intentLocked ? (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
            stroke={ATELIER.accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
            style={{ marginLeft: 2, opacity: 0.8 }}>
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" />
          </svg>
        ) : (
          <motion.svg
            width="9" height="9" viewBox="0 0 24 24" fill="none"
            stroke={ATELIER.inkSoft} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            animate={{ rotate: open ? 180 : 0, opacity: hover || open ? 0.9 : 0.4 }}
            transition={{ duration: 0.2 }}
            style={{ marginLeft: 2 }}
          >
            <path d={ICON.chevronDown} />
          </motion.svg>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 8px)',
              left: 0,
              zIndex: 30,
              minWidth: 240,
              background: ATELIER.bg,
              border: `1px solid ${ATELIER.borderStrong}`,
              borderRadius: 12,
              padding: 5,
              boxShadow: ATELIER.shadowL,
              transformOrigin: 'bottom left',
            }}
          >
            <div style={{
              fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.22em',
              color: ATELIER.inkMuted, fontWeight: 700,
              padding: '8px 10px 6px', textTransform: 'uppercase',
            }}>
              Route to
            </div>
            {MODES.map(mode => {
              const active = mode.id === intent.mode;
              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => { overrideIntent(mode.id); setOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '9px 10px',
                    background: active ? ATELIER.accentSoft : 'transparent',
                    border: 'none', borderRadius: 8,
                    cursor: 'pointer', fontFamily: 'inherit',
                    color: ATELIER.ink, textAlign: 'left',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = ATELIER.bgHover; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{
                    width: 24, height: 24, borderRadius: 6,
                    background: active ? ATELIER.accent : ATELIER.bgHover,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke={active ? '#fff' : ATELIER.inkSoft} strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d={ICON[mode.id]} />
                    </svg>
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: ATELIER.ink, letterSpacing: '-0.005em' }}>
                      {mode.label}
                    </div>
                    <div style={{
                      fontSize: 10.5, color: ATELIER.inkMuted, marginTop: 1,
                      fontFamily: FONTS.mono, letterSpacing: '0.04em',
                    }}>/{mode.slashCmd}</div>
                  </div>
                  {active && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                      stroke={ATELIER.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d={ICON.check} />
                    </svg>
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
